import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  Globe,
  MapPin,
  Users,
} from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { companiesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Developer {
  id: number
  name: string
  headline: string | null
  score: number | null
  linkedin_url: string
}

interface Company {
  id: number
  name: string
  tier: number | null
  tier_name: string
  tier_description: string
  score: number | null
  description: string | null
  headquarters: string | null
  industry: string | null
  website: string | null
  github_url: string | null
  founded_year: number | null
  tech_stack: string[] | null
  logo_url: string | null
  developers: Developer[]
}

interface Props {
  company: Company
}

export default function CompaniesShow({ company }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Companies", href: companiesPath() },
    { title: company.name, href: `/companies/${company.id}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={company.name} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Link
            href={companiesPath()}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>

          {company.logo_url ? (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-white p-2 shadow-sm">
              <img
                src={company.logo_url}
                alt={`${company.name} logo`}
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                }}
              />
            </div>
          ) : (
            <div className="bg-primary/10 text-primary flex h-16 w-16 items-center justify-center rounded-lg">
              <Building2 className="h-8 w-8" />
            </div>
          )}

          <div>
            <h1 className="text-2xl font-bold">{company.name}</h1>
            {company.description && (
              <p className="text-muted-foreground">{company.description}</p>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-xl border p-6">
              <h2 className="mb-4 text-lg font-semibold">Overview</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Building2 className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <p className="font-medium">Tier {company.tier_name}</p>
                    <p className="text-muted-foreground text-sm">
                      {company.tier_description}
                    </p>
                  </div>
                </div>

                {company.headquarters && (
                  <div className="flex items-start gap-3">
                    <MapPin className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <p>{company.headquarters}</p>
                  </div>
                )}

                {company.industry && (
                  <div className="flex items-start gap-3">
                    <Building2 className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <p>{company.industry}</p>
                  </div>
                )}

                {company.founded_year && (
                  <div className="flex items-start gap-3">
                    <Building2 className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <p>Founded {company.founded_year}</p>
                  </div>
                )}

                {company.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Website
                    </a>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Users className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <p className="font-medium">
                      Score: {Math.round(company.score ?? 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {company.tech_stack && company.tech_stack.length > 0 && (
              <div className="rounded-xl border p-6">
                <h2 className="mb-4 text-lg font-semibold">Tech Stack</h2>
                <div className="flex flex-wrap gap-2">
                  {company.tech_stack.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-primary/10 text-primary rounded-full px-3 py-1 text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {company.developers && company.developers.length > 0 && (
              <div className="rounded-xl border p-6">
                <h2 className="mb-4 text-lg font-semibold">
                  Developers ({company.developers.length})
                </h2>
                <div className="divide-y">
                  {company.developers.map((dev) => (
                    <div
                      key={dev.id}
                      className="hover:bg-muted/50 flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="flex-1">
                        <Link
                          href={`/rankings/${dev.id}`}
                          className="font-medium hover:underline"
                        >
                          {dev.name || "Unknown"}
                        </Link>
                        {dev.headline && (
                          <p className="text-muted-foreground text-sm">
                            {dev.headline}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          {Math.round(dev.score ?? 0)}
                        </div>
                      </div>
                      <a
                        href={dev.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border p-6">
              <h2 className="mb-4 text-lg font-semibold">Company Score</h2>
              <div className="text-center">
                <div className="text-4xl font-bold">
                  {Math.round(company.score ?? 0)}
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  Based on tier and enrichment data
                </p>
              </div>
            </div>

            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary text-primary-foreground hover:bg-primary/90 block rounded-lg px-4 py-3 text-center"
              >
                Visit Company Website
              </a>
            )}

            {company.github_url && (
              <a
                href={company.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-muted block rounded-lg border px-4 py-3 text-center"
              >
                View GitHub
              </a>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
